import {DEFAULT_INPUT_KEYS} from '../utils/payment-plan-input-keys';
import {detach} from '../utils/detach';
import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  MinusCircle,
  RefreshCw,
  Send,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { getFinancePreview, runCaseFunction, sendPaymentPlan } from "../api";
import {paymentPlanRateApi} from '../api/payment-plan-rate';
import {resolveAnnualRatePct} from '../utils/payment-plan-interest-rate';
import { formatThousands, isNumericEntry, stripThousands } from "../utils/format-number";
import { fieldRuleErrors, ruleFactorFills } from "../utils/validate";
import {
  DbrEligibility,
  DbrEligibilityTenor,
  FinancePreviewSection,
  FormField,
  ResolvedWidget,
  ResolvedWidgetInterestRate,
} from "../dtos";
import FormFieldInput from "./form-fields";
import {
  CompoundCheckCard,
  FacilitiesTable,
  StepHistoryPanel,
} from "./context-widgets";
import { primaryButtonSx, secondaryButtonSx, fieldInputSx } from "./field-shell";
import {
  DBR_ALL_SECTIONS,
  DBR_ALL_TENOR_COLUMNS,
  DBR_CAPTURE_KEYS,
  DbrCaptureField,
  dbrSums,
  liveDbrAvailable,
  maxLoanTable,
  PaymentFrequency,
  resolvePersonaSlots,
  ServedDbrInputs,
} from "../utils/finance";
import {
  DEFAULT_PAYMENT_PLAN_CONFIG,
  evaluatePaymentPlan,
  hasJudgeableInputs,
  hasRequiredInputs,
  normalizeFrequency,
  OutputFormat,
  PAYMENT_PLAN_DEFAULT_OUTPUTS,
  PAYMENT_PLAN_OUTPUT_META,
  PAYMENT_PLAN_SCHEDULE_COLUMNS,
  PaymentPlanConfig,
  PaymentPlanInputs,
  PaymentPlanResult,
} from "../utils/payment-plan";

/**
 * §Widgets: the finance widget placements of the Finance Request step, ported 1:1 from
 * workflow-manager's own step screen so the CRM tab is fully workable for agents:
 *  - dbrCalculator      → FinanceCalculator (what-if income → max loan per tenor, client-side)
 *  - paymentPlanCalculator → PaymentCalculator (configured equations + schedule + Send to Customer)
 *  - financePreview     → FinancePreviewCard (server-side Send-to-Credit readiness of SAVED data)
 * All math mirrors workflow-manager's libs (utils/finance.ts, utils/payment-plan.ts); the backend
 * recomputes server-side on send, so these are presentation + advisory only.
 */

const fmt2 = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtMoney = (value: number): string =>
  `EGP ${Math.round(value).toLocaleString("en-US")}`;

const fmtOutput = (format: OutputFormat, value: number | undefined): string => {
  if (value === undefined || Number.isNaN(value)) return "—";
  if (format === "percent") return `${(value * 100).toFixed(2)}%`;
  if (format === "number") return Math.round(value).toLocaleString("en-US");
  return fmtMoney(value);
};

/**
 * dbrCalculator — prototype-parity DBR calculator. available = income × threshold% − existing
 * facilities (BOTH modes), max loan per tenor at that tenor's ENFORCED rate (mid-corridor + spread,
 * served by the server so display == the completion guard). Inputs prefill from the case facts.
 */
const FinanceCalculator = ({
  thresholdPct = 50,
  applicants = 1,
  showSecondApplicant,
  dbrInputs,
  eligibility,
  parameters,
  liveFacts,
  onFactChange,
  useServedAllowance = false,
}: {
  thresholdPct?: number;
  applicants?: number;
  showSecondApplicant?: boolean;
  dbrInputs?: ServedDbrInputs;
  eligibility?: DbrEligibility;
  useServedAllowance?: boolean;
  /** §DBR-surface / §DBR-views: the placement's resolved config — sections / tenorColumns / view. */
  parameters?: Record<string, unknown>;
  liveFacts?: Record<string, unknown>;
  /** §DBR-override: edits persist into the step draft under DBR_CAPTURE_KEYS (autosave → the server
   * gives the step's own capture precedence, so the ENFORCED numbers follow the edit). */
  onFactChange?: (key: string, value: unknown) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const joint = showSecondApplicant ?? applicants >= 2;
  // §DBR-surface: which parts render + which table columns — per placement, default everything.
  // Display only: a hidden income row still drives the numbers server-side.
  const visibleSections = (parameters?.sections as string[] | undefined) ?? DBR_ALL_SECTIONS;
  const cols = (parameters?.tenorColumns as string[] | undefined) ?? DBR_ALL_TENOR_COLUMNS;
  const showIncome = visibleSections.includes("income");
  const showAvailable = visibleSections.includes("available");
  const showTable = visibleSections.includes("tenorTable");
  // §Per-persona (WS7): a configured persona set replaces the hardwired pair with one group per persona
  const personaSlots = resolvePersonaSlots(dbrInputs?.personas);
  const personaMode = personaSlots.length > 0;
  const [v, setV] = useState<Record<string, number>>(() => {
    // live-first: reopening the widget keeps unsaved edits the draft already carries
    const liveOr = (field: DbrCaptureField, fallback: number): number => {
      const live = asNumber(liveFacts?.[DBR_CAPTURE_KEYS[field]]);
      return live ?? fallback;
    };
    if (personaMode) {
      const state: Record<string, number> = {};
      for (const slot of personaSlots) {
        state[slot.incomeField] = liveOr(slot.incomeField, slot.income);
        state[slot.existingField] = liveOr(slot.existingField, slot.existing);
      }
      return state;
    }
    return {
      income1: liveOr("income1", dbrInputs?.income1 ?? 0),
      existing1: liveOr("existing1", dbrInputs?.existing1 ?? 0),
      income2: liveOr("income2", dbrInputs?.income2 ?? 0),
      existing2: liveOr("existing2", dbrInputs?.existing2 ?? 0),
    };
  });
  const [whatIfEdited, setWhatIfEdited] = useState(false);
  const set = (key: DbrCaptureField) => (raw: string) => {
    setWhatIfEdited(true);
    setV({ ...v, [key]: parseFloat(raw) || 0 });
    // §DBR-override: the edit rides the step draft under its canonical key — the payment plan's live
    // DBR-fit and (after autosave) the server's enforced numbers both read it. Cleared ⇒ null (delete
    // the capture, reverting to the mapped source on save).
    onFactChange?.(DBR_CAPTURE_KEYS[key], raw.trim() === "" ? null : parseFloat(raw) || 0);
  };

  // §DBR-parity: available = income × threshold% − existing (persona slots when configured; the
  // bureau product-bucket total subtracts additively, never editable here)
  const { income, available: computedAvailable } = dbrSums({
    values: v,
    personaSlots,
    joint,
    productObligations: dbrInputs?.productObligations,
    thresholdPct,
  });
  const available = useServedAllowance && !whatIfEdited ? eligibility?.availableInstallment ?? computedAvailable : computedAvailable;
  // per-tenor ENFORCED rates from the server; fall back to a flat table if eligibility is absent.
  // §DBR-views: 'credit' (default) keeps the served pricing — every row flat at the CHOSEN tenor's
  // rate (the credit sheet model); 'agent' reprices each row at its OWN per-year rate (ownRatePct).
  const agentView = parameters?.view === "agent";
  const tenorRows = eligibility?.tenors?.length
    ? eligibility.tenors.map((t) => {
        const rate = agentView ? (t.ownRatePct ?? t.annualRatePct) : t.annualRatePct;
        return {
          tenorYears: t.tenorYears,
          annualRatePct: rate,
          maxLoan: maxLoanTable(
            available,
            rate,
            [t.tenorYears],
            "MONTHLY" as PaymentFrequency
          )[0].maxLoan,
        };
      })
    : maxLoanTable(available, 18, [1, 2, 3, 4, 5, 6, 7], "MONTHLY").map((r) => ({
        tenorYears: r.tenorYears,
        annualRatePct: 18,
        maxLoan: r.maxLoan,
      }));

  const row = (label: string, key: DbrCaptureField) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 0.75,
        // §Mobile: 200px input + label don't fit a squeezed card — wrap, never clip the value
        flexWrap: "wrap",
        rowGap: 0.5,
        borderBottom: `1px solid ${colors.semantic.border.secondary}`,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 400, color: colors.semantic.text.secondary }}>{label}</Typography>
      {/* §Separators (WM parity): grouped-display text input — no spinner arrows, no wheel decrement */}
      <TextField
        size="small"
        sx={{ ...fieldInputSx(theme), width: 200, maxWidth: "100%" }}
        value={formatThousands(v[key] || "")}
        onChange={(event) => {
          const raw = stripThousands(event.target.value);
          if (isNumericEntry(raw)) set(key)(raw);
        }}
        onKeyDown={(event) => {
          if (["-", "e", "E", "+"].includes(event.key)) event.preventDefault();
        }}
        InputProps={{
          startAdornment: <InputAdornment position="start" sx={{ "& .MuiTypography-root": { fontSize: 12, color: colors.semantic.text.primary } }}>EGP</InputAdornment>,
        }}
        inputProps={{ inputMode: "decimal" }}
      />
    </Box>
  );
  const outRow = (label: string, value: string, highlight = false) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        py: 0.75,
        flexWrap: "wrap",
        px: highlight ? 1 : 0,
        borderRadius: highlight ? 1 : 0,
        bgcolor: highlight ? "#f0fdf4" : undefined,
        borderBottom: highlight
          ? undefined
          : `1px solid ${colors.semantic.border.secondary}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 400,
          color: highlight ? "#166534" : colors.semantic.text.secondary,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: highlight ? "#166534" : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
  // §DBR-parity (prototype): "Current DBR (from i-Score)" is a display-only row; "Product DBR" is
  // the single-mode existing/income ratio (literal '#DIV/0!' before an income is entered).
  const groupHeader = (label: string) => (
    <Typography
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color: colors.semantic.text.secondary,
        bgcolor: colors.semantic.layer.secondary,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        px: 1,
        py: 0.75,
        borderRadius: 0.5,
        mt: 0.5,
      }}
    >
      {label}
    </Typography>
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: theme.nawy.radius.lg }}>
      <Typography sx={{ fontWeight: 400, fontSize: 12, mb: 1, color: colors.semantic.text.primary }}>
        DBR Calculator
        {personaMode
          ? ` — ${personaSlots.map((slot) => slot.label).join(" + ")} (one finance product)`
          : joint
            ? " — Joint Applicants (one finance product)"
            : ""}
      </Typography>
      {/* §Reduced surface (WM parity): visible rows are the income inputs (+ combined total when
          several) and Available Installment Value. The threshold, Max. DBR Value, Product DBR,
          Current DBR (never-wired i-Score stub) and the existing-facilities inputs were removed as
          redundant — existing facilities STILL subtract (they keep their served values and drive
          `available`), they just can't be corrected here. */}
      {showIncome &&
        (personaMode ? (
          <>
            {personaSlots.map((slot) => (
              <Box key={slot.key}>
                {personaSlots.length > 1 && groupHeader(slot.label)}
                {row("Monthly Income", slot.incomeField)}
              </Box>
            ))}
          </>
        ) : joint ? (
          <>
            {groupHeader("Client 1")}
            {row("Monthly Income", "income1")}
            {groupHeader("Client 2")}
            {row("Monthly Income", "income2")}
          </>
        ) : (
          row("Monthly Income", "income1")
        ))}
      {showAvailable && (
        <>
          {(personaMode ? personaSlots.length > 1 : joint) &&
            outRow("Combined Monthly Income", fmt2(income))}
          {outRow("Available Installment Value", fmt2(available), true)}
        </>
      )}

      {income > 0 && available <= 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            p: 1.5,
            mt: 1,
            borderRadius: theme.nawy.radius.lg,
            backgroundColor: colors.semantic.layer.extension.negativeLight,
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: colors.semantic.text.extension.negative }}>
            {/* Copy is the ACTION, not the diagnosis (product ask — Sarah Shetewy, 2026-08-10).
                The paragraph this replaces explained why the eligible loan is 0 and listed the
                ways out; the table above already shows the 0s, so the alert now states only what
                the agent should do about it. */}
            Increase Client Income
          </Typography>
        </Box>
      )}
      {/* §DBR-surface: the table + its columns render per the placement's config */}
      {showTable && (
        <>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 11.5,
              color: colors.semantic.text.secondary,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              mt: 1.5,
              mb: 0.5,
            }}
          >
            Eligible Loan Amount by Tenor
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {cols.includes("tenor") && (
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Tenor</TableCell>
                )}
                {cols.includes("rate") && (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
                    Profit rate (annual %)
                  </TableCell>
                )}
                {cols.includes("maxLoan") && (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
                    Max Loan Amount (EGP)
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tenorRows.map((r) => (
                <TableRow key={r.tenorYears}>
                  {cols.includes("tenor") && (
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {r.tenorYears} {r.tenorYears === 1 ? "year" : "years"}
                    </TableCell>
                  )}
                  {cols.includes("rate") && (
                    <TableCell
                      align="right"
                      sx={{ fontSize: 12.5, color: colors.semantic.text.secondary }}
                    >
                      {r.annualRatePct}%
                    </TableCell>
                  )}
                  {cols.includes("maxLoan") && (
                    <TableCell
                      align="right"
                      sx={{ fontSize: 12.5, fontWeight: 600 }}
                    >
                      {fmt2(r.maxLoan)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Paper>
  );
};

/** §UnitType validation (WM payment-plan-calc.service parity). §PlanRules (2026-08-06): the
 * loan-amount caps ARE tenant config in WM (finance-calculator-config, admin-tunable) — these
 * constants are only the WM DEFAULTS, kept as the fallback for older WM deploys that don't yet
 * serve `eligibility.planRules`. The down-payment band is genuinely hardcoded in WM. The
 * profitability bands (NPV / escrow / final-cash) need the partner-rate model and stay server-side. */
const MAX_REMAINING_COMMERCIAL = 15_000_000;
const MAX_REMAINING_RESIDENTIAL = 8_000_000;
const MIN_DOWN_PAYMENT_PCT = 20;
const MAX_DOWN_PAYMENT_PCT = 100;

const asNumber = (v: unknown): number | undefined => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)))
    return Number(v);
  return undefined;
};

type DbrFit = { text: string; color: string };
type TenorRows = DbrEligibilityTenor[];
type PlanOwnedField = { key: string; field: FormField };
type PlanParams = Partial<PaymentPlanConfig> & {
  inputKeys?: Record<string, string>;
  annualRatePctOverride?: number | null;
  /** §DBR-block: WM's per-placement guard flag — strict `true` only */
  blockWhenExceedsDbr?: boolean;
  /** §Tenor-rates (engine #250): per-tenor annual rate from the tenant finance config */
  tenorRatesPct?: { tenorYears: number; ratePct: number }[];
};

// §DBR-parity: live form values win over the saved facts, so the plan + validation react as the
// agent types (prototype behavior); the saved (server-resolved) inputs are the fallback.
const resolveInputKeys = (p: PlanParams): Record<string, string> => ({ ...DEFAULT_INPUT_KEYS, ...(p.inputKeys ?? {}) });
const resolvePlanInputs = (
  inputKeys: Record<string, string>,
  facts: Record<string, unknown>,
  saved?: PaymentPlanInputs
): PaymentPlanInputs => {
  const fallback: PaymentPlanInputs = saved ?? {};
  return {
    cashPrice: asNumber(facts[inputKeys.cashPrice]) ?? fallback.cashPrice,
    downPayment: asNumber(facts[inputKeys.downPayment]) ?? fallback.downPayment,
    tenorYears: asNumber(facts[inputKeys.tenorYears]) ?? fallback.tenorYears,
    frequency: (facts[inputKeys.frequency] as string | undefined) ?? fallback.frequency,
    leadingMonths: facts[inputKeys.leadingMonths] ?? fallback.leadingMonths,
  };
};

/**
 * §WM-source-of-truth: the interest rate is the TENANT's number, never ours.
 *
 * The formulas stay here so the preview recomputes as the agent types, but every variable they
 * consume has to come from workflow-manager. WM prices each tenor from FinanceCalculatorConfig
 * (`midCorridorRatePct` + that tenor's spread) and already ships the result on
 * `dbrEligibility.tenors` — the very same number the matching FinancePaymentPlan row carries
 * (19.5 + 18.1 = 37.6 for 1 year, 19.5 + 12.5 = 32 for 2, and so on).
 *
 * The evaluator's own `annualRatePct` default is 32, which is the TWO-YEAR rate. So the live
 * preview happened to be right at 2 years and was wrong at every other tenor — 5.6 points out on
 * a 1-year plan, 4.5 out on a 10-year one — while the plan WM saved used the correct per-tenor
 * rate. That is the drift between the two calculators.
 *
 * An application-specific Finance override wins first when present, including zero. Otherwise,
 * WM's PRICED tenor rate wins over the widget registry's `constants.annualRatePct`.
 * Both are WM, but they are not equal authorities — the registry constant is a single literal in
 * the widget definition (today 32) that cannot be right for ten tenors at once, whereas the
 * priced rate is derived per tenor from the tenant's live FinanceCalculatorConfig and matches the
 * FinancePaymentPlan row the server actually charges. The constant stays as the fallback for a
 * tenor WM served no row for. `ownRatePct` is preferred over `annualRatePct` for the same reason
 * the what-if repricing does: served rows may be flat-priced at the chosen tenor, and we want
 * THIS tenor's own rate.
 */
const resolvePlanConfig = (p: PlanParams, annualRatePct: number | undefined): PaymentPlanConfig => {
  const base: PaymentPlanConfig = {
    constants: { ...DEFAULT_PAYMENT_PLAN_CONFIG.constants, ...(p.constants ?? {}) },
    formulas: { ...DEFAULT_PAYMENT_PLAN_CONFIG.formulas, ...(p.formulas ?? {}) },
    schedule: { ...DEFAULT_PAYMENT_PLAN_CONFIG.schedule, ...(p.schedule ?? {}) },
    scheduleColumns: p.scheduleColumns ?? DEFAULT_PAYMENT_PLAN_CONFIG.scheduleColumns,
    sendToCustomer: {
      ...DEFAULT_PAYMENT_PLAN_CONFIG.sendToCustomer,
      ...(p.sendToCustomer ?? {}),
    },
  };
  if (annualRatePct === undefined) return base;
  return { ...base, constants: { ...base.constants, annualRatePct } };
};

/**
 * §PaymentPlan-outputs: which HEADLINE values this placement shows. `scheduleColumns` has always
 * let WM hide SCHEDULE columns per placement, but the summary rows were a hard-coded list here,
 * so a credit-facing surface had no way to hide Interest Revenue or Flat Rate. WM now serves the
 * symmetric key; unset ⇒ every output, so existing placements are unchanged. Display only — a
 * hidden output is still computed.
 *
 * Unset ⇒ WM's own four, NOT the whole catalogue. See PAYMENT_PLAN_DEFAULT_OUTPUTS: WM's headline
 * grid is a hardcoded four and its step screen never reads `outputColumns`, so defaulting to
 * "everything" here made an unconfigured placement show eight cards against WM's four.
 */
const visiblePlanOutputs = (p: PlanParams, rateEditorActive = false): typeof PAYMENT_PLAN_OUTPUT_META => {
  const shown = p.outputColumns?.length ? p.outputColumns : PAYMENT_PLAN_DEFAULT_OUTPUTS;
  // A rate decision needs the cost of credit even when the placement keeps the usual four
  // headline outputs. Keep ordinary placements and their configured output set unchanged.
  const visible = rateEditorActive ? new Set([...shown, 'interestRevenue', 'flatRate']) : new Set(shown);
  return PAYMENT_PLAN_OUTPUT_META.filter(meta => visible.has(meta.key)).map(meta =>
    rateEditorActive && meta.key === 'interestRevenue' ? {...meta, label: 'Total interest'} :
    rateEditorActive && meta.key === 'flatRate' ? {...meta, label: 'Flat rate'} : meta
  );
};

/**
 * §DerivedLoanAmount: what actually gets written. Loan Amount is computed, so the submission must
 * carry the derived figure rather than whatever the field happened to hold — otherwise a plan
 * saved before this fix (or with a stale value on the step) keeps a loan amount its own
 * installments contradict, and that number is what Send-to-Credit and the customer PDF read.
 */
const derivedLoan = (planInputs: PaymentPlanInputs): number =>
  Math.max(0, (planInputs.cashPrice ?? 0) - (planInputs.downPayment ?? 0));
const downPaymentPct = (planInputs: PaymentPlanInputs): number => {
  const cashPrice = planInputs.cashPrice ?? 0;
  return cashPrice > 0 ? ((planInputs.downPayment ?? 0) / cashPrice) * 100 : 0;
};
const roundedTenor = (tenorYears: number | undefined): number | undefined =>
  tenorYears !== undefined && tenorYears > 0 ? Math.round(tenorYears) : undefined;

// With ruleFacts present the LOCAL evaluation is the authority — falling back to the
// step-form's fieldErrors would resurrect a STALE error from the last committed values
// (a fixed field kept its old red line and the buttons stayed dead — live bite, 2026-08-06).
const authoredRuleErrors = (
  ownedFields: PlanOwnedField[] | undefined,
  liveRuleError: (key: string) => string | undefined
): string[] => (ownedFields ?? []).map(({ key }) => liveRuleError(key)).filter((message): message is string => Boolean(message));
const hasAuthoredRules = (ownedFields: PlanOwnedField[] | undefined): boolean =>
  (ownedFields ?? []).some(({ field }) => field.validate !== undefined && field.validate !== null);

interface PlanRuleContext {
  judgeable: boolean;
  authoredErrors: string[];
  authoredRules: boolean;
  unitType: string;
  ruleLoan: number;
  ruleDpPct: number;
  /** §PlanRules: the tenant's live caps off the widget payload — the server enforces exactly
   * these on save, so the fallback pre-check reads the same numbers (the constants only cover
   * WM deploys that don't serve them yet). */
  eligibility?: DbrEligibility;
}
/** The hard-coded §UnitType port. It caps at the WM DEFAULTS (15M/8M), which out-shouted a Builder
 * config of 60M — so it runs ONLY when the template authors no validate rule of its own. */
const defaultPlanRuleError = (ctx: PlanRuleContext): string | null => {
  const capCommercial = ctx.eligibility?.planRules?.maxRemainingCommercial ?? MAX_REMAINING_COMMERCIAL;
  const capResidential = ctx.eligibility?.planRules?.maxRemainingResidential ?? MAX_REMAINING_RESIDENTIAL;
  if (ctx.unitType.includes("commercial") && ctx.ruleLoan > capCommercial) {
    return `Loan amount exceeds the commercial cap (${fmt2(capCommercial)})`;
  }
  if (ctx.unitType.includes("residential") && ctx.ruleLoan > capResidential) {
    return `Loan amount exceeds the residential cap (${fmt2(capResidential)})`;
  }
  if (ctx.ruleDpPct > MAX_DOWN_PAYMENT_PCT) {
    return "Decrease Downpayment or check with the finance team";
  }
  if (ctx.ruleDpPct < MIN_DOWN_PAYMENT_PCT) {
    return `Increase Downpayment or check with the finance team (minimum ${MIN_DOWN_PAYMENT_PCT}% of the cash price)`;
  }
  return null;
};
/**
 * §UnitType validation (live bite, 2026-08-05): the WM calculator rejects plans off the
 * Commercial/Residential choice — the CRM modal ran none of it. The input-only rules run
 * LIVE here, WM wording kept; a violation blocks Save/Share like an out-of-range tenor.
 *
 * §Rule-arrays (product ask, 2026-08-06): when the TEMPLATE authors validate rules on the
 * plan's own fields (the current template: "Loan Amount ≤ 60M for Commercial", "DP ≥ 20% for
 * Ijara"…), those ARE the validation — authored numbers, authored messages, engine-enforced
 * on save.
 */
const resolvePlanRuleError = (
  ctx: PlanRuleContext,
  draftPreview: boolean,
  draftValidationError: string | undefined
): string | null => {
  // The isolated draft owns its authored validation, including products allowing zero down payment.
  if (draftPreview && draftValidationError !== undefined) return draftValidationError || null;
  if (ctx.judgeable && ctx.authoredErrors.length) return ctx.authoredErrors[0];
  if (ctx.judgeable && !ctx.authoredRules) return defaultPlanRuleError(ctx);
  return null;
};

/**
 * §72-parity (live bite, 2026-08-04): validation runs BEFORE calculation, exactly like WM's
 * calculator. A tenor of 12,000,000 years used to sail through — the red "Tenor must be between
 * 1 and 7 years" line rendered, but the schedule (EGP 46-trillion totals) and the Save/Share
 * buttons stayed live. An out-of-range tenor now produces NO result at all: no chips, no
 * schedule, and both actions dim (they key on `result`).
 */
const isTenorBlocked = (liveTenor: number | undefined, tenorRows: TenorRows): boolean => {
  if (liveTenor === undefined) return false;
  if (tenorRows.length) return !tenorRows.some((t) => t.tenorYears === liveTenor);
  // no eligibility served (DBR not run yet) → nothing to enforce, but a tenor no mortgage
  // could ever carry is still garbage input, not a plan
  return liveTenor > 100;
};
const evaluatePlanSafely = (
  enabled: boolean,
  config: PaymentPlanConfig,
  planInputs: PaymentPlanInputs
): { result: PaymentPlanResult | null; error: string | null } => {
  if (!enabled) return { result: null, error: null };
  try {
    return { result: evaluatePaymentPlan(config, planInputs), error: null };
  } catch (e) {
    return { result: null, error: (e as Error).message };
  }
};

/**
 * §DBR-override (live bite, 2026-08-05: income typed in the DBR widget never reached this modal —
 * the fit read the SERVED eligibility until save+refetch): when the draft carries DBR capture
 * edits, recompute the available installment from the LIVE inputs — the same formula the widget
 * and the server use. Untouched slots fall back to the server-resolved dbrInputs.
 */
const resolveLiveAvailable = (
  disabled: boolean | undefined,
  liveFacts: Record<string, unknown> | undefined,
  eligibility: DbrEligibility | undefined,
  dbrInputs: ServedDbrInputs | undefined
): number | null => {
  const hasLiveDbrEdits =
    !disabled && liveFacts != null && Object.values(DBR_CAPTURE_KEYS).some((k) => liveFacts[k] !== undefined);
  if (!hasLiveDbrEdits || !eligibility || !dbrInputs) return null;
  return liveDbrAvailable({ liveFacts, dbrInputs, thresholdPct: eligibility.thresholdPct, asNumber });
};

/**
 * §Sheet-parity: served rows may be flat-priced at the CHOSEN tenor's rate — but saving with
 * THIS tenor makes it the chosen one, so the enforced max prices at the tenor's OWN rate
 * (ownRatePct). Reprice whenever live DBR edits exist OR the own rate differs from the priced
 * rate; otherwise the server's number stands.
 */
const enforcedMaxLoan = (
  tenorRows: TenorRows,
  liveTenor: number,
  liveAvailable: number | null,
  knownAvailable: number | null
): number => {
  const row = tenorRows.find((t) => t.tenorYears === liveTenor);
  const whatIfRate = row ? (row.ownRatePct ?? row.annualRatePct) : 0;
  const needsWhatIfReprice =
    row !== undefined && whatIfRate !== row.annualRatePct && knownAvailable !== null;
  return row && (liveAvailable !== null || needsWhatIfReprice) && knownAvailable !== null
    ? maxLoanTable(knownAvailable, whatIfRate, [liveTenor], "MONTHLY")[0].maxLoan
    : (row?.maxLoan ?? 0);
};
interface DbrFitContext {
  eligibility?: DbrEligibility;
  invalidDraft: boolean;
  cashPrice?: number;
  liveTenor?: number;
  liveAvailable: number | null;
  liveLoan: number;
  neutralColor: string;
}
/**
 * §DBR-parity: the prototype's live loan-vs-DBR validation, against the ENFORCED eligibility the
 * completion guard uses — identical wording ('Enter cash price and tenor' / tenor bounds / ⚠ / ✓).
 */
const resolveDbrFit = (ctx: DbrFitContext): DbrFit | null => {
  const { eligibility, invalidDraft, cashPrice, liveTenor, liveAvailable, liveLoan } = ctx;
  if (!eligibility || invalidDraft) return null;
  if (!cashPrice || cashPrice <= 0 || !liveTenor) {
    return { text: "Enter cash price and tenor", color: ctx.neutralColor };
  }
  const tenorRows = eligibility.tenors;
  if (!tenorRows.some((t) => t.tenorYears === liveTenor)) {
    const minT = tenorRows[0]?.tenorYears ?? 1;
    const maxT = tenorRows[tenorRows.length - 1]?.tenorYears ?? 7;
    return { text: `Tenor must be between ${minT} and ${maxT} years`, color: "#b91c1c" };
  }
  const maxLoan = enforcedMaxLoan(tenorRows, liveTenor, liveAvailable, liveAvailable ?? eligibility.availableInstallment);
  return liveLoan > maxLoan
    ? { text: `⚠ Exceeds DBR-eligible amount for ${liveTenor} yr: max ${fmt2(maxLoan)}`, color: "#b91c1c" }
    : { text: `✓ Within DBR-eligible amount for ${liveTenor} yr (max ${fmt2(maxLoan)})`, color: "#166534" };
};

/**
 * §DBR-block: WM's per-placement opt-in to dimming the plan's write actions while the live plan
 * exceeds the DBR-eligible amount. WM has been serving this (`blockWhenExceedsDbr`, strict `true`
 * only) and the live deal-flow template sets it on the DBR & Payment Plan step — but nothing here
 * ever read it, so the guard an admin switched on was silently doing nothing.
 *
 * `dbrFit` already carries the verdict for the chosen tenor, so the exceed state is read off that
 * rather than recomputed — one source, no chance of the caption and the buttons disagreeing.
 */
const isDbrBlocked = (p: PlanParams, dbrFit: DbrFit | null): boolean =>
  p.blockWhenExceedsDbr === true && dbrFit?.text.startsWith("⚠") === true;

const planCtaLabel = (draftPreview: boolean, hasResult: boolean): string => {
  if (draftPreview) return "View draft schedule";
  return hasResult ? "Edit plan" : "Create plan";
};
const emptyPlanMessage = (
  draftPreview: boolean,
  draftValidationError: string | undefined,
  error: string | null
): string => {
  if (draftValidationError && draftPreview) return draftValidationError;
  if (error) return `Calculator configuration error: ${error}`;
  return "No plan yet — open the plan editor to create one.";
};

/**
 * §Modals: the design (Figma 12107:56541 card + 12107:56750 "Preview payment plan", 948×826)
 * splits this widget in two — the step card carries a COMPACT SAVED SUMMARY plus an "Edit plan"
 * link, and the plan's inputs, numbers, schedule and send action all live in the modal behind it.
 */
interface PlanSummaryCardProps {
  result: PaymentPlanResult | null;
  planInputs: PaymentPlanInputs;
  frequency: ReturnType<typeof normalizeFrequency>;
  draftPreview: boolean;
  invalidDraft: boolean;
  tenorBlocked: boolean;
  disabled?: boolean;
  canOpenReadOnlyPlan?: boolean;
  readOnlyPlan?: boolean;
  dbrFit: DbrFit | null;
  error: string | null;
  draftValidationError?: string;
  onOpenPreview: () => void;
}
const PlanSummaryCard = ({
  result,
  planInputs,
  frequency,
  draftPreview,
  invalidDraft,
  tenorBlocked,
  disabled,
  canOpenReadOnlyPlan,
  readOnlyPlan,
  dbrFit,
  error,
  draftValidationError,
  onOpenPreview,
}: PlanSummaryCardProps) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  // design (Slot 1 plan rows, 12107:57505+): Body/md — 14/20, and Weight/Bold resolves to Poppins
  // SemiBold (600). At 700 the labels rendered true Bold and the 22px leading stretched the block.
  const summaryRow = (label: string, value: string) => (
    <Typography
      key={label}
      sx={{ fontSize: 14, lineHeight: "20px", color: colors.semantic.text.primary }}
    >
      <Box component="span" sx={{ fontWeight: 600 }}>{`${label}: `}</Box>
      {value}
    </Typography>
  );
  return (
    // design Slot 1 (12107:57524) is radius xl (12), not lg — §Radius (text fields keep 12 where
    // Figma says 8) is about INPUTS and doesn't cover cards. The 1px outline stays: the design's
    // white slot sits on the widget's #F4F7F8 ground, while the CRM step card renders its expanded
    // body in a WHITE inset panel (step-card.tsx), so a borderless white card would vanish there.
    <Paper variant="outlined" sx={{ p: 2, borderRadius: theme.nawy.radius.xl }}>
      {/* §EditPlan (product ask, 2026-08-06): the modal CTA is a blue button at the card's
          TOP-RIGHT, not a text link under the summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5, flexWrap: "wrap", rowGap: 0.5 }}
      >
        {/* design: the plan eyebrow is Body/xs - Regular (11/16), not 12/18 */}
        <Typography
          sx={{ fontSize: 11, lineHeight: "16px", color: colors.semantic.text.secondary }}
        >
          Payment plan
        </Typography>
        <Button
          variant="contained"
          size="small"
          // The modal's inputs are already read-only on a settled or view-only step, so the CTA
          // says the same thing rather than opening an editor where nothing can be changed.
          disabled={draftPreview ? !result || invalidDraft || tenorBlocked : Boolean(disabled) && !canOpenReadOnlyPlan}
          onClick={onOpenPreview}
          sx={primaryButtonSx(theme)}
        >
          {readOnlyPlan ? 'View plan' : planCtaLabel(draftPreview, Boolean(result))}
        </Button>
      </Stack>
      {result ? (
        <>
          {summaryRow("Unit price", fmtMoney(planInputs.cashPrice ?? 0))}
          {summaryRow("Loan amount", fmtMoney(result.summary.loanAmount))}
          {summaryRow("Down payment", fmtMoney(planInputs.downPayment ?? 0))}
          {summaryRow(
            `${frequency === "Monthly" ? "Monthly" : "Quarterly"} payment`,
            fmtMoney(result.summary.installment)
          )}
          {summaryRow(
            "Tenor",
            `${planInputs.tenorYears} years (${result.schedule.length} payments)`
          )}
        </>
      ) : (
        <Typography sx={{ fontSize: 13, color: colors.semantic.text.secondary }}>
          {emptyPlanMessage(draftPreview, draftValidationError, error)}
        </Typography>
      )}
      {/* §DBR-parity: the live loan-vs-DBR verdict stays ON THE CARD even though the design's card
          omits it — it is the gate on sending the plan to the client, and burying a blocking
          validation behind a modal (unopenable before a plan computes) would hide it exactly when
          the agent needs it. */}
      {dbrFit && (
        <Typography sx={{ mt: 1, fontSize: 12, lineHeight: "18px", color: dbrFit.color }}>
          {dbrFit.text}
        </Typography>
      )}
    </Paper>
  );
};

/**
 * `ownedFields` are the step form's Payment Plan fields, which StepForm stops rendering on the
 * card and hands to this widget instead, so the modal's left column is the only place a plan is
 * typed — exactly as the design has it.
 */
interface PlanInputsColumnProps {
  eligibility?: DbrEligibility;
  liveAvailable: number | null;
  ownedFields?: PlanOwnedField[];
  calculatedKeys?: ReadonlySet<string>;
  inputKeys: Record<string, string>;
  ruleLoan: number;
  modalFacts: Record<string, unknown>;
  modalTouched: Set<string>;
  disabled?: boolean;
  onFactChange?: (key: string, value: unknown) => void;
  liveRuleError: (key: string) => string | undefined;
  setModalDraft: Dispatch<SetStateAction<Record<string, unknown>>>;
}
const PlanInputsColumn = ({
  eligibility,
  liveAvailable,
  ownedFields,
  calculatedKeys,
  inputKeys,
  ruleLoan,
  modalFacts,
  modalTouched,
  disabled,
  onFactChange,
  liveRuleError,
  setModalDraft,
}: PlanInputsColumnProps) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  return (
    <Box
      sx={{
        width: { mobile: "100%", desktop: 440 },
        p: 2,
        // §OneScroll: the sheet (<744px, where MobileModal renders) is ONE continuous scroll — the
        // design has the max-loan panel, the inputs and the outputs flow down a single column. Only
        // the side-by-side dialog (>=744px, CommonModal, which clips instead of scrolling) gives
        // each column its own scroller.
        overflowY: { mobile: "visible", tablet: "auto" },
        borderRight: { desktop: `1px solid ${colors.semantic.border.secondary}` },
      }}
    >
      {Boolean(eligibility?.tenors?.length) && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: theme.nawy.radius.xl,
            // design Layer/Secondary is #F4F7F8 = white[200]; the theme's layer.secondary token
            // maps to navy[50] (#EEF3F5) — same sidestep step-card.tsx makes for the card ground.
            backgroundColor: colors.primitives.white[200],
          }}
        >
          {/* design 12107:65792: the panel title is Body/xs - Regular (11/16) */}
          <Typography
            sx={{ fontSize: 11, lineHeight: "16px", color: colors.semantic.text.secondary }}
          >
            Maximum loan amount
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {(eligibility?.tenors ?? []).map((tenor) => (
              <Typography
                key={tenor.tenorYears}
                sx={{ fontSize: 14, lineHeight: "20px", color: colors.semantic.text.primary }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {`${tenor.tenorYears} ${tenor.tenorYears === 1 ? "year" : "years"} `}
                </Box>
                {fmtMoney((liveAvailable ?? eligibility?.availableInstallment) != null ? maxLoanTable((liveAvailable ?? eligibility?.availableInstallment)!, tenor.ownRatePct ?? tenor.annualRatePct, [tenor.tenorYears], 'MONTHLY')[0].maxLoan : tenor.maxLoan)}
              </Typography>
            ))}
          </Stack>
          <Typography
            sx={{
              mt: 1,
              fontSize: 11,
              lineHeight: "16px",
              color: colors.semantic.text.extension.negative,
            }}
          >
            *These amounts could changed based on credit
          </Typography>
        </Box>
      )}

      {ownedFields?.length ? (
        <Stack spacing={2}>
          {ownedFields.map(({ key, field }) => {
            // §DerivedLoanAmount (WM parity): a CALCULATED field is READ-ONLY and refilled on every
            // change — workflow-manager's renderer dims exactly that (form-renderer.tsx:711:
            // `ro = readOnly || locked || (isDerived && !deriveEditable)`) and its derive effect
            // overwrites anything typed there. `isDerived` is membership of the compiled derive
            // plan, NOT the presence of a `derive`: one the engine can't compute (a conditional
            // whose branch is unarmed, an unresolvable reference) stays typeable rather than
            // locking a box nothing fills. Keyed off the template, never off the Loan Amount key,
            // so a builder opting into `deriveEditable` is honoured with no further code change.
            //
            // Loan Amount is the current template's case: `src: "calc"`, derive
            // `[Cash Price] - [Down Payment (20%)]`, no `deriveEditable`. The modal computes its
            // value itself because its edits are a LOCAL draft (§ManualPlan) the step form's derive
            // engine only sees on commit — where `evaluateDerivedFills` refills every calculated
            // field (`claimsDeriveOverride` keeps them out of `touched`), so any other calculated
            // field lands correct on Save even though only the loan is previewed live here.
            const calculated =
              Boolean(calculatedKeys?.has(key)) && field.deriveEditable !== true;
            const isDerivedLoan = key === inputKeys.loanAmount;
            return (
              <FormFieldInput
                key={key}
                field={field}
                value={isDerivedLoan ? ruleLoan : modalFacts?.[key]}
                error={calculated ? undefined : liveRuleError(key)}
                // §ManualPlan: typing edits the LOCAL draft only — Save plan commits it.
                // §DBR-parity: the edit also carries the suggestions its own rule targets take
                // (Down Payment = factor × Cash Price) until the agent edits them here.
                onChange={(next) => {
                  modalTouched.add(key);
                  setModalDraft((draft) => ({
                    ...draft,
                    [key]: next,
                    ...ruleFactorFills(ownedFields, key, next, modalTouched),
                  }));
                }}
                disabled={
                  Boolean(disabled) || calculated || field.mode === "view" || !onFactChange
                }
              />
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
};

interface PlanDetailColumnProps {
  dbrFit: DbrFit | null;
  liveLoan: number;
  planRuleError: string | null;
  ready: boolean;
  tenorBlocked: boolean;
  error: string | null;
  result: PaymentPlanResult | null;
  planInputs: PaymentPlanInputs;
  frequency: ReturnType<typeof normalizeFrequency>;
  visibleOutputs: typeof PAYMENT_PLAN_OUTPUT_META;
  scheduleCols: typeof PAYMENT_PLAN_SCHEDULE_COLUMNS;
  showDueDate?: boolean;
  toast: string;
  onToastClose: () => void;
}
const PlanDetailColumn = ({
  dbrFit,
  liveLoan,
  planRuleError,
  ready,
  tenorBlocked,
  error,
  result,
  planInputs,
  frequency,
  visibleOutputs,
  scheduleCols,
  showDueDate,
  toast,
  onToastClose,
}: PlanDetailColumnProps) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const [showSchedule, setShowSchedule] = useState(false);
  const periodLabel = frequency === "Monthly" ? "month" : "quarter";
  return (
    <Box
      sx={{
        flex: 1,
        py: 2,
        minWidth: 0,
        // §OneScroll: no scroller of its own on the sheet — see planInputsColumn
        overflowY: { mobile: "visible", tablet: "auto" },
        // design (12107:65827 "Outputs"): on the sheet the results sit on a full-bleed tinted band
        // that separates them from the white inputs above — Layer/Subtle #EDF2F3, which the theme's
        // layer.secondary (navy[50] #EEF3F5) matches. The negative margin cancels MobileModal's own
        // 16px padding so the band runs edge to edge, and the 32px inner padding keeps its content
        // aligned with the inputs column above it.
        backgroundColor: { mobile: colors.semantic.layer.secondary, tablet: "transparent" },
        mx: { mobile: -2, tablet: 0 },
        px: { mobile: 4, tablet: 2 },
      }}
    >
      {/* §DBR-parity: the live Loan Amount + DBR-fit line ("Validated against the DBR-eligible
          amount for the chosen tenor"), updating as the agent types in the Payment Plan section */}
      {dbrFit && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: 0.75,
            mb: 0.75,
            borderBottom: `1px dashed ${colors.semantic.border.secondary}`,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 400, color: colors.semantic.text.secondary }}>
              Loan Amount = Cash − Down Payment
            </Typography>
            <Typography sx={{ fontSize: 12, color: dbrFit.color }}>
              {dbrFit.text}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
            {fmt2(liveLoan)}
          </Typography>
        </Box>
      )}

      {/* §UnitType validation: WM's calculator verdict, keyed on the Commercial/Residential
          choice — a violating plan shows the reason and cannot be saved or shared */}
      {planRuleError && (
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#b91c1c", py: 0.5 }}>
          ⚠ {planRuleError}
        </Typography>
      )}
      {!ready && (
        <Typography
          sx={{ fontSize: 13, color: colors.semantic.text.secondary, py: 1 }}
        >
          Fill in the Payment Plan section (cash price and tenor) to generate
          the plan.
        </Typography>
      )}
      {/* no-eligibility leg of §72-parity — with eligibility present, dbrFit already says the bounds */}
      {ready && tenorBlocked && !dbrFit && (
        <Typography sx={{ fontSize: 12.5, color: "#b91c1c", py: 1 }}>
          Tenor is out of range — enter a realistic number of years.
        </Typography>
      )}
      {error && (
        <Typography sx={{ fontSize: 12.5, color: colors.semantic.text.extension.negative, py: 1 }}>
          Calculator configuration error: {error}
        </Typography>
      )}

      {result && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
            <Chip
              size="small"
              variant="outlined"
              label={`Cash Price: ${fmtMoney(planInputs.cashPrice ?? 0)}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Down Payment: ${fmtMoney(planInputs.downPayment ?? 0)}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Tenor: ${planInputs.tenorYears} years`}
            />
            <Chip size="small" variant="outlined" label={`Frequency: ${frequency}`} />
            {/* the annual rate chip was removed (product ask, 2026-08-06) — internal pricing,
                not something the agent needs to see or quote */}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { mobile: "1fr 1fr", desktop: "repeat(4, 1fr)" },
              gap: 1,
              mb: 1.5,
            }}
          >
            {visibleOutputs.map((meta) => (
              <Box
                key={meta.key}
                sx={{
                  border: `1px solid ${colors.semantic.border.secondary}`,
                  borderRadius: 1.5,
                  px: 1.25,
                  py: 0.9,
                  // design: the summary tiles are white cards ON the tinted outputs band
                  backgroundColor: colors.semantic.layer.primary,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.semantic.text.secondary,
                    mb: 0.25,
                  }}
                >
                  {meta.label}
                  {meta.key === "installment" || meta.key === "requiredIncome"
                    ? ` / ${periodLabel}`
                    : ""}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {fmtOutput(meta.format, (meta.key === "numPeriods" ? result.schedule.length : result.summary[meta.key]))}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            onClick={() => setShowSchedule((s) => !s)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              userSelect: "none",
              color: colors.semantic.text.secondary,
            }}
          >
            {showSchedule ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Installment schedule ({result.schedule.length} payments)
            </Typography>
          </Box>
          <Collapse in={showSchedule}>
            <Box
              sx={{
                // §OneScroll: on the sheet the schedule flows inside the single scroll (design
                // 12107:65835 — the table just continues down the page); only the dialog caps it.
                // The horizontal scroller stays on every size: the schedule is wider than a phone.
                maxHeight: { mobile: "none", tablet: 360 },
                overflowX: "auto",
                overflowY: { mobile: "visible", tablet: "auto" },
                mt: 0.5,
                border: `1px solid ${colors.semantic.border.secondary}`,
                borderRadius: 1.5,
                // white rows against the tinted outputs band
                backgroundColor: colors.semantic.layer.primary,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11.5 }}>#</TableCell>
                    {showDueDate && <TableCell sx={{fontWeight: 700, fontSize: 11.5}}>Due date</TableCell>}
                    {scheduleCols.map((c) => (
                      <TableCell
                        key={c.key}
                        align="right"
                        sx={{ fontWeight: 700, fontSize: 11.5 }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.schedule.map((r) => (
                    <TableRow key={r.period}>
                      <TableCell sx={{ fontSize: 12 }}>{r.period}</TableCell>
                      {showDueDate && <TableCell sx={{fontSize: 12}}>{r.dueDate ?? '—'}</TableCell>}
                      {scheduleCols.map((c) => (
                        <TableCell
                          key={c.key}
                          align="right"
                          sx={{
                            fontSize: 12,
                            fontWeight: c.key === "installment" ? 600 : 400,
                          }}
                        >
                          {fmtMoney(r[c.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={onToastClose}
        message={toast}
      />
    </Box>
  );
};

interface PlanModalFooterProps {
  draftPreview: boolean;
  sendEnabled: boolean;
  result: PaymentPlanResult | null;
  sending: boolean;
  saving: boolean;
  rateActionPending: boolean;
  planRuleError: string | null;
  tenorBlocked: boolean;
  dbrBlocked: boolean;
  onSend: () => void;
  onSave: () => void;
}
const PlanModalFooter = ({
  draftPreview,
  sendEnabled,
  result,
  sending,
  saving,
  rateActionPending,
  planRuleError,
  tenorBlocked,
  dbrBlocked,
  onSend,
  onSave,
}: PlanModalFooterProps) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      spacing={1.5}
      useFlexGap
      sx={{
        px: 2.5,
        py: 2,
        flexWrap: "wrap",
        rowGap: 1.5,
        // §MobileQA (iOS Safari): this footer lives INSIDE MobileModal's scrolling child box,
        // so it scrolled with the content instead of staying pinned the way the design shows it.
        // Sticking it to the bottom of that scroller keeps the verbs reachable at any scroll
        // position; the negative offset cancels the sheet's own 16px padding so it sits flush.
        position: "sticky",
        bottom: -16,
        zIndex: 1,
        backgroundColor: colors.semantic.layer.primary,
        borderTop: `1px solid ${colors.semantic.border.secondary}`,
      }}
    >
      {!draftPreview && sendEnabled && (
        <Button
          variant="outlined"
          disabled={!result || sending || rateActionPending || Boolean(planRuleError) || dbrBlocked}
          startIcon={sending ? <CircularProgress size={14} /> : <Send size={14} />}
          onClick={onSend}
          sx={secondaryButtonSx(theme)}
        >
          {sending ? "Sending…" : "Share with client"}
        </Button>
      )}
      {!draftPreview && <Button
        variant="contained"
        disableElevation
        // §72-parity: an out-of-range tenor must not be saveable either — the draft would arm
        // the completion guard with a plan no lender can price.
        // §UnitType validation: a rule-violating plan must not be saveable either
        // §DBR-block: and not while the plan exceeds the DBR-eligible amount, when the
        // placement asked for that guard
        // §Incomplete: and not before the plan actually computes — Share already required this
        disabled={saving || rateActionPending || !result || tenorBlocked || Boolean(planRuleError) || dbrBlocked}
        startIcon={saving ? <CircularProgress size={14} /> : undefined}
        onClick={onSave}
        sx={primaryButtonSx(theme)}
      >
        Save plan
      </Button>}
    </Stack>
  );
};

/** paymentPlanCalculator — configured equations + amortization schedule + Send to Customer,
 * with the prototype's LIVE DBR-fit validation (loan vs the enforced max for the chosen tenor). */
const PaymentCalculator = ({
  parameters,
  inputs,
  caseUid,
  stepUid,
  liveFacts,
  eligibility,
  dbrInputs,
  ownedFields,
  calculatedKeys,
  onFactChange,
  commitFacts,
  ruleFacts,
  disabled,
  draftPreview = false,
  draftValidationError,
  saveDraft,
  fieldErrors,
  interestRate,
  review = false,
}: {
  parameters?: Record<string, unknown>;
  inputs?: PaymentPlanInputs;
  caseUid: string;
  /** the pressing step — the server authorizes the send against THIS step's placement config */
  stepUid?: string;
  /** live form values from the step form, keyed `<Section>.<Label>` — validation updates as the agent types */
  liveFacts?: Record<string, unknown>;
  eligibility?: DbrEligibility;
  /** §DBR-override: the server-resolved DBR inputs — the fallback layer for the LIVE DBR-fit
   * recompute while the agent edits the DBR widget on the same step */
  dbrInputs?: ServedDbrInputs;
  /** §Modals: the plan's own input fields, moved off the card into this modal (design 12107:56750) */
  ownedFields?: { key: string; field: FormField }[];
  /** §Derive: the keys the step form's derive engine is CALCULATING — rendered read-only here, the
   * same rule WM's form renderer applies (`isDerived && !deriveEditable`). */
  calculatedKeys?: ReadonlySet<string>;
  onFactChange?: (key: string, value: unknown) => void;
  /** §ManualPlan (product ask, 2026-08-06): commit the modal's local draft into the step form —
   * called ONLY by Save plan / Share, never while typing (no autosave escape for invalid plans) */
  commitFacts?: (entries: Record<string, unknown>) => void;
  /** case facts incl. upstream `@s…` keys, for evaluating the template's `when` rule branches */
  ruleFacts?: Record<string, unknown>;
  disabled?: boolean;
  /** Isolated revision preview: view the schedule without any official mutation. */
  draftPreview?: boolean;
  draftValidationError?: string;
  /** persists the step draft — the design's "Save plan" writes the plan, it doesn't just close */
  saveDraft?: () => Promise<unknown>;
  /** §Rules: live template validate-rule verdicts for the owned fields */
  fieldErrors?: Record<string, string>;
  interestRate?: ResolvedWidgetInterestRate;
  review?: boolean;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState("");
  /** §Modals: the numbers + schedule live in the "Preview payment plan" modal (Figma 12107:56750) */
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rateState, setRateState] = useState(interestRate);
  const [rateIdentityStale, setRateIdentityStale] = useState(false);
  const [rateDraft, setRateDraft] = useState<string | null>(null);
  const [rateBusy, setRateBusy] = useState(false);
  const [rateError, setRateError] = useState('');
  useEffect(() => {
    setRateState(interestRate);
    setRateIdentityStale(false);
  }, [interestRate]);
  // §ManualPlan: the modal's edits live HERE until Save plan / Share commits them — closing the
  // modal discards them, so an invalid draft can never leak onto the card or into the step.
  const [modalDraft, setModalDraft] = useState<Record<string, unknown>>({});
  // §DBR-parity: the keys edited IN THE MODAL. A rule-driven suggestion (the Down Payment following
  // the Cash Price at the template's factor) never overwrites one of these — the same "manual wins"
  // rule the step form applies through its own touched set.
  const modalTouched = useMemo(() => new Set<string>(), []);
  const modalFacts = { ...liveFacts, ...modalDraft };
  const queryClient = useQueryClient();

  const p = (parameters ?? {}) as PlanParams;
  const inputKeys = resolveInputKeys(p);
  // A Finance review can carry an older placement input snapshot. Its currentPlan is the
  // application plan being reviewed and is also the base for an authorized rate preview.
  const planInputs = review && rateState
    ? rateState.currentPlan.inputs as PaymentPlanInputs
    : resolvePlanInputs(inputKeys, modalFacts, inputs);
  const parsedRateDraft = rateDraft === null || rateDraft.trim() === '' ? null : Number(rateDraft);
  const rateDraftValid = rateDraft === null || (rateDraft.trim() !== '' && Number.isFinite(parsedRateDraft) && parsedRateDraft !== null && parsedRateDraft >= 0);
  const previewRate = rateDraft === null ? (rateState ? rateState.customAnnualRatePct : p.annualRatePctOverride) : parsedRateDraft;
  const rateResolution = (() => {
    try {
      return {config: resolvePlanConfig(p, resolveAnnualRatePct(previewRate, p, eligibility, planInputs.tenorYears)), error: null};
    } catch (cause) {
      return {config: resolvePlanConfig(p, undefined), error: (cause as Error).message};
    }
  })();
  const config = rateResolution.config;
  const visibleOutputs = visiblePlanOutputs(p, Boolean(rateState?.canEdit && !draftPreview));

  const ready = hasRequiredInputs(planInputs);
  const judgeable = hasJudgeableInputs(planInputs);
  const frequency = normalizeFrequency(planInputs.frequency);
  const ruleLoan = derivedLoan(planInputs);
  const withDerivedLoanAmount = (
    draft: Record<string, unknown>
  ): Record<string, unknown> =>
    ready ? {...draft, [inputKeys.loanAmount]: ruleLoan} : draft;
  // §ManualPlan: rules re-judge on the modal's LOCAL draft — the step-form's fieldErrors only
  // know committed values, which no longer change while the agent types here.
  const modalRuleErrors = useMemo(
    () =>
      ownedFields?.length
        ? fieldRuleErrors(
            [{ name: "", fields: ownedFields.map(({ field }) => field) }],
            {
              ...ruleFacts,
              ...liveFacts,
              ...modalDraft,
              // §Derive: the calculated Loan Amount this modal DISPLAYS is the one its rules must
              // be judged on. Edits live in a local draft the step form's derive engine never sees
              // until Save, so without this the authored caps would be read against the last saved
              // loan while the agent watches a different number on screen — and a plan that busts
              // the cap would keep Save live.
              ...(ready ? { [inputKeys.loanAmount]: ruleLoan } : {}),
            }
          )
        : {},
    [ownedFields, ruleFacts, liveFacts, modalDraft, ready, inputKeys.loanAmount, ruleLoan]
  );
  const liveRuleError = (key: string): string | undefined =>
    ruleFacts ? modalRuleErrors[key] : fieldErrors?.[key];
  const planRuleError = resolvePlanRuleError(
    {
      judgeable,
      authoredErrors: authoredRuleErrors(ownedFields, liveRuleError),
      authoredRules: hasAuthoredRules(ownedFields),
      unitType: String(modalFacts[inputKeys.propertyUsage] ?? "").toLowerCase(),
      ruleLoan,
      ruleDpPct: downPaymentPct(planInputs),
      eligibility,
    },
    draftPreview,
    draftValidationError
  );
  const sendEnabled = Boolean(config.sendToCustomer?.enabled);
  const scheduleCols = PAYMENT_PLAN_SCHEDULE_COLUMNS.filter((c) =>
    config.scheduleColumns.includes(c.key)
  );

  const liveTenor = roundedTenor(planInputs.tenorYears);
  const tenorBlocked = isTenorBlocked(liveTenor, eligibility?.tenors ?? []);
  const invalidDraft = draftPreview && Boolean(draftValidationError);
  const calculated = rateResolution.error
    ? {result: null, error: rateResolution.error}
    : evaluatePlanSafely(ready && !tenorBlocked && !invalidDraft && rateDraftValid, config, planInputs);
  const savedInputs = rateState?.currentPlan.inputs;
  const sameSavedInputs = savedInputs &&
    savedInputs.cashPrice === planInputs.cashPrice &&
    savedInputs.downPayment === planInputs.downPayment &&
    savedInputs.tenorYears === planInputs.tenorYears &&
    normalizeFrequency(savedInputs.frequency) === frequency &&
    (savedInputs.leadingMonths ?? 0) === (planInputs.leadingMonths ?? 0);
  const result: PaymentPlanResult | null = !rateResolution.error && !draftPreview && rateDraft === null && sameSavedInputs && ready && !invalidDraft && !tenorBlocked
    ? rateState!.currentPlan as PaymentPlanResult : calculated.result;
  const error = calculated.error;
  const effectiveScheduleCols = rateDraft === null && sameSavedInputs && rateState
    ? PAYMENT_PLAN_SCHEDULE_COLUMNS.filter(c => rateState.currentPlan.scheduleColumns.includes(c.key))
    : scheduleCols;
  const showDueDate = Boolean(rateDraft === null && sameSavedInputs && rateState?.currentPlan.scheduleColumns.includes('dueDate'));
  const termsDirty = Object.keys(modalDraft).length > 0;
  const rateActionPending = rateDraft !== null || rateBusy || rateIdentityStale || rateState?.generation?.status === 'generating' || rateState?.generation?.status === 'failed';

  const updateRate = async (retry = false, restore = false): Promise<void> => {
    if (!stepUid || !rateState?.canEdit || rateBusy || termsDirty || (!retry && !restore && !rateDraftValid)) return;
    setRateBusy(true);
    setRateError('');
    try {
      const identity = {expectedVersion: rateState.version, expectedPlanFingerprint: rateState.planFingerprint};
      const updated = retry
        ? await paymentPlanRateApi.retry(stepUid, identity)
        : await paymentPlanRateApi.apply(stepUid, {annualRatePct: restore ? null : parsedRateDraft, ...identity});
      setRateState(updated);
      setRateDraft(null);
      detach(queryClient.invalidateQueries({queryKey: ['nawy-now']}));
    } catch (cause) {
      setRateError((cause as {response?: {data?: {message?: string}}}).response?.data?.message ?? 'Could not update the interest rate. Reload the latest plan and try again.');
    } finally {
      setRateBusy(false);
    }
  };

  /**
   * §PlanFile: render the plan document from the SAVED inputs and file it on the case, so the
   * payment-plan file card below the calculator has something to download. Both plan verbs produce
   * it: Share does it server-side already (sendPaymentPlan regenerates the attachment it sends),
   * and Save runs it explicitly here — a saved plan the agent cannot hand over is half a plan.
   *
   * Best-effort by contract, exactly like WM's own "Generate plan file" button: a `skipped` outcome
   * (the function declined — no cash price, a settled step) reports its reason, and a failure must
   * not undo or mask the save that already succeeded.
   */
  const generatePlanFile = async (): Promise<void> => {
    if (draftPreview) return;
    try {
      const res = await runCaseFunction(caseUid, "generatePaymentPlanPdf");
      setToast(
        res.skipped
          ? `Plan saved. The plan file was not generated: ${res.skipped.reason}`
          : "Plan saved and the plan file is ready to download."
      );
    } catch {
      setToast("Plan saved, but the plan file could not be generated.");
    } finally {
      // The run files the document on the step's placement — refetch so the card below shows it.
      detach(queryClient.invalidateQueries({ queryKey: ["nawy-now"] }));
    }
  };

  const send = async (): Promise<void> => {
    if (draftPreview || rateActionPending) return;
    setSending(true);
    try {
      // §ManualPlan: the server sends the SAVED submission — commit + persist the draft first
      if (Object.keys(modalDraft).length) {
        commitFacts?.(withDerivedLoanAmount(modalDraft));
        await saveDraft?.();
        setModalDraft({});
      }
      const res = await sendPaymentPlan(caseUid, stepUid);
      setToast(
        res.notified
          ? "Payment plan sent to the customer."
          : "Plan saved — no customer contact on file to notify."
      );
      // The send files the generated plan file onto the step's document placement — refetch so the
      // document card shows it instead of waiting for an unrelated invalidation.
      detach(queryClient.invalidateQueries({ queryKey: ["nawy-now"] }));
    } catch (e) {
      setToast(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to send the payment plan."
      );
    } finally {
      setSending(false);
    }
  };

  const liveLoan = ruleLoan;
  const liveAvailable = resolveLiveAvailable(disabled, liveFacts, eligibility, dbrInputs);
  const dbrFit = resolveDbrFit({
    eligibility,
    invalidDraft,
    cashPrice: planInputs.cashPrice,
    liveTenor,
    liveAvailable,
    liveLoan,
    neutralColor: String(colors.semantic.text.secondary ?? "#64748b"),
  });
  const dbrBlocked = isDbrBlocked(p, dbrFit);

  const savePlan = (): void => {
    if (draftPreview || rateActionPending) return;
    // §ManualPlan: the ONLY write path — commit the local draft, then persist
    commitFacts?.(withDerivedLoanAmount(modalDraft));
    if (!saveDraft) {
      setModalDraft({});
      setPreviewOpen(false);
      return;
    }
    setSaving(true);
    detach(
      saveDraft()
        .then(() => {
          // A term save changes the plan fingerprint. Wait for the refetched capability before
          // allowing another rate write, so an old identity cannot price a newer plan.
          if (termsDirty && rateState) {
            setRateState(undefined);
            setRateIdentityStale(true);
          }
          return generatePlanFile();
        })
        .then(() => {
          setModalDraft({});
          setRateDraft(null);
          setRateError('');
          setPreviewOpen(false);
        })
        .catch(() => undefined)
        .finally(() => setSaving(false))
    );
  };

  return (
    <>
      <PlanSummaryCard
        result={result}
        planInputs={planInputs}
        frequency={frequency}
        draftPreview={draftPreview}
        invalidDraft={invalidDraft}
        tenorBlocked={tenorBlocked}
        disabled={disabled}
        canOpenReadOnlyPlan={Boolean(rateState)}
        readOnlyPlan={Boolean(!draftPreview && disabled && !rateState?.canEdit)}
        dbrFit={dbrFit}
        error={error}
        draftValidationError={draftValidationError}
        onOpenPreview={() => setPreviewOpen(true)}
      />
      <ResponsiveModal
        open={previewOpen}
        onClose={() => {
          // §ManualPlan: closing without saving discards the draft — the card keeps the saved plan
          setModalDraft({});
          setRateDraft(null);
          setRateError('');
          setPreviewOpen(false);
        }}
        title="Preview payment plan"
      >
        {rateState && !draftPreview && <Box sx={{p: 2, borderBottom: '1px solid #ddd'}}>
          <Typography sx={{fontWeight: 600}}>Annual interest rate: {rateState.effectiveAnnualRatePct}%</Typography>
          <Typography variant="caption">Standard rate: {rateState.standardAnnualRatePct}%{rateState.customAnnualRatePct !== null ? ' · Finance adjusted' : ''}</Typography>
          {rateState.currentPlan.adjustedBy && <Typography variant="caption" display="block">Adjusted by {rateState.currentPlan.adjustedBy}</Typography>}
          {rateState.canEdit && stepUid && <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1}}>
            <TextField size="small" type="number" label="Annual interest rate (%)" inputProps={{min: 0, step: 'any'}}
              value={rateDraft ?? (rateState.customAnnualRatePct ?? rateState.standardAnnualRatePct)}
              onChange={event => setRateDraft(event.target.value)} error={!rateDraftValid} />
            <Button disabled={rateBusy || termsDirty || !rateDraftValid || rateDraft === null || rateDraft.trim() === ''} onClick={() => detach(updateRate())}>Apply rate</Button>
            {rateState.customAnnualRatePct !== null && <Button disabled={rateBusy || termsDirty} onClick={() => detach(updateRate(false, true))}>Restore standard</Button>}
            {rateDraft !== null && <Button onClick={() => setRateDraft(null)}>Discard rate preview</Button>}
          </Stack>}
          {termsDirty && rateState.canEdit && <Typography variant="caption" display="block">Save or discard the plan terms before applying a rate.{rateDraft !== null ? ' Discard the rate preview to enable Save plan.' : ''}</Typography>}
          {rateState.generation?.status === 'generating' && <Typography variant="caption">Generating updated plan…</Typography>}
          {rateState.generation?.status === 'failed' && <Stack direction="row" spacing={1} alignItems="center"><Typography color="error" variant="caption">{rateState.generation.error ?? 'Plan file generation failed.'}</Typography>{rateState.canEdit && stepUid && <Button disabled={rateBusy || termsDirty} onClick={() => detach(updateRate(true))}>Retry file</Button>}</Stack>}
          {rateError && <Typography color="error" variant="caption" role="alert">{rateError}</Typography>}
        </Box>}
        <Stack
          direction={{ mobile: "column", desktop: "row" }}
          sx={{
            width: { mobile: "100%", desktop: 948 },
            // §OneScroll: capping the body at 70vh on the sheet is what forced the two columns to
            // scroll independently (and pushed the footer past the sheet's clipped edge). Uncapped,
            // the body flows and MobileModal's own scroll box — the one the sticky footer below
            // sticks to — is the single scroller, exactly as the design has it. The dialog
            // (>=744px) still needs the cap: CommonModal clips its overflow instead of scrolling.
            maxHeight: { mobile: "none", tablet: "70vh" },
          }}
        >
          <PlanInputsColumn
            eligibility={eligibility}
            liveAvailable={liveAvailable}
            ownedFields={ownedFields}
            calculatedKeys={calculatedKeys}
            inputKeys={inputKeys}
            ruleLoan={ruleLoan}
            modalFacts={modalFacts}
            modalTouched={modalTouched}
            disabled={disabled}
            onFactChange={onFactChange}
            liveRuleError={liveRuleError}
            setModalDraft={setModalDraft}
          />
          <PlanDetailColumn
            dbrFit={dbrFit}
            liveLoan={liveLoan}
            planRuleError={planRuleError}
            ready={ready}
            tenorBlocked={tenorBlocked}
            error={error}
            result={result}
            planInputs={planInputs}
            frequency={frequency}
            visibleOutputs={visibleOutputs}
            scheduleCols={effectiveScheduleCols}
            showDueDate={showDueDate}
            toast={toast}
            onToastClose={() => setToast("")}
          />
        </Stack>
        <PlanModalFooter
          draftPreview={draftPreview || review || Boolean(disabled)}
          sendEnabled={sendEnabled}
          result={result}
          sending={sending}
          saving={saving}
          rateActionPending={rateActionPending}
          planRuleError={planRuleError}
          tenorBlocked={tenorBlocked}
          dbrBlocked={dbrBlocked}
          onSend={() => detach(send())}
          onSave={savePlan}
        />
      </ResponsiveModal>
    </>
  );
};

const PREVIEW_STATUS: Record<
  FinancePreviewSection["status"],
  { label: string; color: "success" | "warning" | "default"; icon: React.ReactNode }
> = {
  DONE: {
    label: "Done",
    color: "success",
    icon: <CheckCircle2 size={16} color="#16a34a" />,
  },
  INCOMPLETE: {
    label: "Incomplete",
    color: "warning",
    icon: <CircleDashed size={16} color="#d97706" />,
  },
  NOT_APPLICABLE: {
    label: "N/A",
    color: "default",
    icon: <MinusCircle size={16} color="#94a3b8" />,
  },
};

/** financePreview — Send-to-Credit readiness of the case's SAVED data (save the draft, then Refresh). */
const FinancePreviewCard = ({ caseUid }: { caseUid: string }) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  // §Realtime: lives under the ["nawy-now"] prefix so SSE invalidation and the poll floor re-check
  // readiness like every other read. §Refresh-signal: the button still shows the round-trip running
  // (isFetching), or a same-verdict response reads as "the button does nothing".
  const {
    data: preview = null,
    error: queryError,
    refetch,
    isFetching: refreshing,
  } = useQuery({
    queryKey: ["nawy-now", "finance-preview", caseUid],
    queryFn: () => getFinancePreview(caseUid),
    keepPreviousData: true,
  });
  const error = queryError instanceof Error ? queryError.message : "";
  const load = () => refetch();

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: theme.nawy.radius.lg }}>
      {/* §Mobile: title + chip + Refresh wrap as whole units — never one word per line */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap", rowGap: 0.5 }}>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: 12,
            flex: 1,
            color: colors.semantic.text.primary,
            whiteSpace: "nowrap",
          }}
        >
          Send-to-Credit preview
        </Typography>
        {preview && (
          <Chip
            size="small"
            color={preview.complete ? "success" : "warning"}
            label={preview.complete ? "Ready to send" : "Incomplete"}
          />
        )}
        <Button
          size="small"
          disabled={refreshing}
          startIcon={
            refreshing ? <CircularProgress size={13} /> : <RefreshCw size={13} />
          }
          onClick={() => detach(load())}
          sx={{ textTransform: "none" }}
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </Box>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
      {(preview?.sections ?? []).map((section) => (
        <Box
          key={section.key}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 0.85,
            borderBottom: `1px solid ${colors.semantic.border.secondary}`,
          }}
        >
          {PREVIEW_STATUS[section.status].icon}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {section.label}
            </Typography>
            {section.detail && (
              <Typography variant="caption" color="text.secondary">
                {section.detail}
              </Typography>
            )}
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={PREVIEW_STATUS[section.status].color}
            label={PREVIEW_STATUS[section.status].label}
          />
        </Box>
      ))}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        Save the draft, then Refresh to re-check.
      </Typography>
    </Paper>
  );
};

const numOr = (v: unknown, d: number): number =>
  typeof v === "number" ? v : v != null && v !== "" ? Number(v) : d;

/** Dispatcher: renders a resolved widget placement (mirrors workflow-manager's StepWidget switch).
 * `liveFacts` = the step form's current values, so widget validation reacts as the agent types.
 * `stepUid` is needed by placements whose data hangs off the STEP rather than the case (stepHistory);
 * `onCaseChanged` lets a writing widget (compoundCheck) refresh the board it just invalidated. */
const StepWidget = ({
  widget,
  liveFacts,
  stepUid,
  disabled,
  draftPreview = false,
  draftValidationError,
  onCaseChanged,
  saveDraft,
  ownedFields,
  calculatedKeys,
  onFactChange,
  commitFacts,
  ruleFacts,
  fieldErrors,
}: {
  widget: ResolvedWidget;
  liveFacts?: Record<string, unknown>;
  stepUid?: string;
  disabled?: boolean;
  /** Isolated revision preview: view the schedule without any official mutation. */
  draftPreview?: boolean;
  draftValidationError?: string;
  onCaseChanged?: () => void;
  saveDraft?: () => Promise<unknown>;
  /** §Modals: the plan-input fields this widget owns — the card hides them, the modal renders them */
  ownedFields?: { key: string; field: FormField }[];
  /** §Derive: the keys the step form's derive engine is calculating — rendered read-only */
  calculatedKeys?: ReadonlySet<string>;
  onFactChange?: (key: string, value: unknown) => void;
  /** §ManualPlan: commit the plan modal's local draft (Save/Share only, never on keystroke) */
  commitFacts?: (entries: Record<string, unknown>) => void;
  /** case facts incl. upstream `@s…` keys, for the template's `when` rule branches */
  ruleFacts?: Record<string, unknown>;
  /** §Rules: live template validate-rule verdicts keyed by field key */
  fieldErrors?: Record<string, string>;
}) => {
  const p = widget.parameters ?? {};
  switch (widget.widget) {
    case "dbrCalculator":
      return (
        <FinanceCalculator useServedAllowance={draftPreview}
          thresholdPct={numOr(
            widget.dbrEligibility?.thresholdPct ?? p.thresholdPct,
            50
          )}
          applicants={numOr(p.applicants, 1)}
          showSecondApplicant={widget.showSecondApplicant}
          dbrInputs={widget.dbrInputs}
          eligibility={widget.dbrEligibility}
          // §DBR-surface/§DBR-views: sections / tenorColumns / view / personas ride parameters
          parameters={widget.parameters}
          liveFacts={liveFacts}
          onFactChange={disabled ? undefined : onFactChange}
        />
      );
    case "paymentPlanCalculator":
      return (
        <PaymentCalculator
          parameters={widget.parameters}
          inputs={widget.inputs as PaymentPlanInputs | undefined}
          interestRate={widget.interestRate}
          review={widget.review}
          caseUid={widget.caseUid}
          stepUid={stepUid}
          liveFacts={liveFacts}
          eligibility={widget.dbrEligibility}
          dbrInputs={widget.dbrInputs}
          ownedFields={ownedFields}
          calculatedKeys={calculatedKeys}
          onFactChange={draftPreview ? undefined : onFactChange}
          commitFacts={draftPreview ? undefined : commitFacts}
          ruleFacts={ruleFacts}
          disabled={disabled}
          draftPreview={draftPreview}
          draftValidationError={draftValidationError}
          saveDraft={draftPreview ? undefined : saveDraft}
          fieldErrors={fieldErrors}
        />
      );
    case "financePreview":
      return <FinancePreviewCard caseUid={widget.caseUid} />;
    // §Widgets (context): placements that describe the case rather than compute finance — see
    // context-widgets.tsx. Before these existed the dispatcher's default branch swallowed them,
    // so the step rendered with a field silently missing.
    case "compoundCheck":
      return (
        <CompoundCheckCard
          widget={widget}
          caseUid={widget.caseUid}
          disabled={disabled}
          onChecked={onCaseChanged}
          liveFacts={liveFacts}
          saveDraft={saveDraft}
        />
      );
    case "iscoreFacilities":
      return <FacilitiesTable facilities={widget.facilities} />;
    case "stepHistory":
      return (
        <StepHistoryPanel
          stepUid={stepUid}
          groupBySourceStep={p.defaultView === "grouped"}
        />
      );
    default:
      // still unported (e.g. creditReviewPreview) — hide quietly rather than render a broken shell
      return null;
  }
};

export default StepWidget;
